import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const MatchStatusIndicator = () => {
  const [activeMatches, setActiveMatches] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Simulate real-time match data
  useEffect(() => {
    const mockMatches = [
      {
        id: 1,
        title: 'Pelada do Sábado',
        venue: 'Campo do Morumbi',
        date: '2025-10-18',
        time: '14:00',
        status: 'confirmed',
        attendees: {
          confirmed: 8,
          pending: 4,
          total: 12
        },
        payment: {
          amount: 25.00,
          paid: 6,
          pending: 6,
          total: 12
        }
      },
      {
        id: 2,
        title: 'Racha de Domingo',
        venue: 'Quadra da Vila',
        date: '2025-10-19',
        time: '16:00',
        status: 'pending',
        attendees: {
          confirmed: 5,
          pending: 7,
          total: 12
        },
        payment: {
          amount: 20.00,
          paid: 3,
          pending: 9,
          total: 12
        }
      }
    ];

    setActiveMatches(mockMatches);

    // Simulate WebSocket updates
    const interval = setInterval(() => {
      setActiveMatches(prev => prev?.map(match => ({
        ...match,
        attendees: {
          ...match?.attendees,
          confirmed: Math.min(match?.attendees?.confirmed + Math.floor(Math.random() * 2), match?.attendees?.total),
          pending: Math.max(match?.attendees?.pending - Math.floor(Math.random() * 2), 0)
        }
      })));
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'cancelled': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'cancelled': return 'XCircle';
      default: return 'Calendar';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendente';
      case 'cancelled': return 'Cancelada';
      default: return 'Agendada';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })?.format(date);
  };

  const totalPendingPayments = activeMatches?.reduce((sum, match) => 
    sum + (match?.payment?.pending * match?.payment?.amount), 0
  );

  const totalPendingConfirmations = activeMatches?.reduce((sum, match) => 
    sum + match?.attendees?.pending, 0
  );

  if (activeMatches?.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {/* Status Badge */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 bg-card border border-border rounded-lg px-3 py-2 shadow-elevation-1 hover:shadow-elevation-2 transition-standard"
      >
        <div className="flex items-center space-x-1">
          <Icon name="Calendar" size={16} className="text-primary" />
          <span className="text-sm font-medium text-card-foreground">
            {activeMatches?.length}
          </span>
        </div>
        
        {(totalPendingPayments > 0 || totalPendingConfirmations > 0) && (
          <div className="flex items-center space-x-1">
            {totalPendingPayments > 0 && (
              <div className="flex items-center space-x-1 text-warning">
                <Icon name="CreditCard" size={12} />
                <span className="text-xs font-data">
                  {formatCurrency(totalPendingPayments)}
                </span>
              </div>
            )}
            
            {totalPendingConfirmations > 0 && (
              <div className="flex items-center space-x-1 text-warning">
                <Icon name="Users" size={12} />
                <span className="text-xs font-medium">
                  {totalPendingConfirmations}
                </span>
              </div>
            )}
          </div>
        )}
        
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={14} 
          className="text-muted-foreground" 
        />
      </button>
      {/* Expanded Details */}
      {isExpanded && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsExpanded(false)}
          />
          
          <div className="absolute top-full mt-2 right-0 w-80 bg-popover border border-border rounded-lg shadow-elevation-3 z-50 animate-fade-in">
            <div className="p-4 border-b border-border">
              <h3 className="font-heading font-semibold text-popover-foreground">
                Peladas Ativas
              </h3>
              <p className="text-sm text-muted-foreground">
                {activeMatches?.length} partida{activeMatches?.length > 1 ? 's' : ''} agendada{activeMatches?.length > 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {activeMatches?.map((match) => (
                <div
                  key={match?.id}
                  className="p-4 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer transition-standard"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-popover-foreground">
                        {match?.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {match?.venue}
                      </p>
                      <p className="text-sm text-muted-foreground font-data">
                        {formatDate(match?.date)} • {match?.time}
                      </p>
                    </div>
                    
                    <div className={`flex items-center space-x-1 ${getStatusColor(match?.status)}`}>
                      <Icon name={getStatusIcon(match?.status)} size={16} />
                      <span className="text-xs font-medium">
                        {getStatusText(match?.status)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Attendance Status */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Confirmados
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-success">
                        {match?.attendees?.confirmed}
                      </span>
                      <span className="text-sm text-muted-foreground">/</span>
                      <span className="text-sm text-muted-foreground">
                        {match?.attendees?.total}
                      </span>
                      {match?.attendees?.pending > 0 && (
                        <>
                          <span className="text-sm text-muted-foreground mx-1">•</span>
                          <span className="text-sm text-warning">
                            {match?.attendees?.pending} pendente{match?.attendees?.pending > 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Payment Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="CreditCard" size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Pagamentos
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-data text-success">
                        {formatCurrency(match?.payment?.paid * match?.payment?.amount)}
                      </span>
                      <span className="text-sm text-muted-foreground">/</span>
                      <span className="text-sm font-data text-muted-foreground">
                        {formatCurrency(match?.payment?.total * match?.payment?.amount)}
                      </span>
                      {match?.payment?.pending > 0 && (
                        <>
                          <span className="text-sm text-muted-foreground mx-1">•</span>
                          <span className="text-sm font-data text-warning">
                            {formatCurrency(match?.payment?.pending * match?.payment?.amount)} pendente
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-3 space-y-2">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-success h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(match?.attendees?.confirmed / match?.attendees?.total) * 100}%` 
                        }}
                      />
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(match?.payment?.paid / match?.payment?.total) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t border-border">
              <button className="w-full text-sm text-primary hover:text-primary/80 font-medium transition-standard">
                Ver todas as peladas
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MatchStatusIndicator;